// ============================================================================
//  ConditionDetail.js — condition view with a tab per child library.
// ----------------------------------------------------------------------------
//  The uniform child shape means each tab is just <LibraryTable category=...>.
//  Adding a new category later is a one-liner in CHILD_TABS.
// ============================================================================

import { useState } from 'react';
import { AgePill, SourcePill, CareSettingPill } from './Templates';
import LibraryTable from './LibraryTable';
import NarrativeTable from './NarrativeTable';
import PendingReviewPanel from './PendingReviewPanel';

// Ordered clinically: how a note gets built in a real consult.
//   • narratives   → the whole documentation set (4 sections that MUST match)
//   • medications  → independent picks
//   • labs         → independent picks
// The old per-category doc tabs (complaint/history/exam/other_docs) are
// superseded by the narratives tab so the four sections can't drift apart.
const CHILD_TABS = [
  { id: 'narratives',  label: 'Documentation Sets',
    hint: 'Complaint + History + Examination + Other Documentation — always matched.' },
  { id: 'medications', label: 'Medications',
    hint: 'Drug · dose · frequency · duration · route' },
  { id: 'labs',        label: 'Labs',
    hint: 'Lab test + expected result phrasing' },
];

export default function ConditionDetail({ condition, conditionId, onBack, onChanged }) {
  const [tab, setTab] = useState(CHILD_TABS[0].id);
  const [pendingOpen, setPendingOpen] = useState(false);
  // When true, the drawer opens filtered to whichever category the reviewer
  // is coming from — makes the "just clicked Suggest with AI" flow instant.
  const [pendingCategory, setPendingCategory] = useState('');

  if (!condition) {
    return (
      <div className="tp-page">
        <button className="tp-back" onClick={onBack}>← Back to library</button>
        <div className="tp-empty">Loading condition…</div>
      </div>
    );
  }

  return (
    <div className="tp-page">
      <div className="tp-detail-head">
        <button className="tp-back" onClick={onBack}>← Back</button>
        <div className="tp-detail-title">
          <h2>{condition.name}</h2>
          <div className="tp-detail-meta">
            <SourcePill condition={condition} />
            <AgePill scope={condition.age_scope} />
            <CareSettingPill setting={condition.care_setting} />
            {condition.source_code && <span className="tp-chip">{condition.source_code}</span>}
            {condition.category && <span className="tp-chip">{condition.category}</span>}
          </div>
        </div>
        <div className="tp-detail-actions">
          <button className="tp-btn tp-btn-ghost" onClick={() => setPendingOpen(true)}>
            Review AI queue
          </button>
        </div>
      </div>

      <div className="tp-tabs">
        {CHILD_TABS.map((t) => (
          <button
            key={t.id}
            className={`tp-tab ${tab === t.id ? 'is-on' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'narratives' ? (
        <NarrativeTable
          conditionId={conditionId}
          conditionAgeScope={condition.age_scope}
          onAiSuggested={() => {
            setPendingCategory('narratives');
            setPendingOpen(true);
          }}
        />
      ) : (
        <LibraryTable
          conditionId={conditionId}
          conditionName={condition.name}
          conditionAgeScope={condition.age_scope}
          category={tab}
          categoryLabel={CHILD_TABS.find((t) => t.id === tab)?.label || tab}
          hint={CHILD_TABS.find((t) => t.id === tab)?.hint}
          onAiSuggested={() => {
            setPendingCategory(tab);
            setPendingOpen(true);
          }}
        />
      )}

      {pendingOpen && (
        <PendingReviewPanel
          conditionId={conditionId}
          conditionName={condition.name}
          initialCategory={pendingCategory}
          onClose={() => setPendingOpen(false)}
          onAccepted={onChanged}
        />
      )}
    </div>
  );
}
